/**
 * Parses custom tags from overview markdown content.
 *
 * Supported tags:
 *   <#ExecutiveSummary#> ... <#/ExecutiveSummary#>
 *   <#Todo list#> ... <#/Todo list#>          (legacy — raw markdown list)
 *   <#TodoListPoint#> ... <#/TodoListPoint#>  (repeatable, one per item)
 *   <#Detail#> ... <#/Detail#>                (detail wrapper section)
 *   <#DetailPoint#> ... <#/DetailPoint#>      (repeatable, inside Detail)
 *
 * Returns an object with:
 *   - executiveSummary: string
 *   - todoList: string              (legacy raw content)
 *   - todoPoints: string[]          (individual TodoListPoint items)
 *   - detail: string                (raw Detail block, if no DetailPoints)
 *   - detailPoints: string[]        (individual DetailPoint items)
 *   - remaining: string
 *   - sections: array of { tag, content } in document order
 */
export function parseOverviewTags(raw) {
  if (!raw) return {
    executiveSummary: '', todoList: '', todoPoints: [],
    detail: '', detailPoints: [], remaining: '', sections: [],
  }

  const TAG_PATTERNS = [
    { key: 'executiveSummary', open: '<#ExecutiveSummary#>', close: '<#/ExecutiveSummary#>' },
    { key: 'todoList', open: '<#Todo list#>', close: '<#/Todo list#>' },
    { key: 'detail', open: '<#Detail#>', close: '<#/Detail#>' },
  ]

  const result = {
    executiveSummary: '',
    todoList: '',
    todoPoints: [],
    detail: '',
    detailPoints: [],
    remaining: '',
    sections: [],
  }

  let remaining = raw

  for (const { key, open, close } of TAG_PATTERNS) {
    const openIdx = remaining.indexOf(open)
    if (openIdx === -1) continue

    const contentStart = openIdx + open.length
    const closeIdx = remaining.indexOf(close, contentStart)
    if (closeIdx === -1) continue

    const content = remaining.substring(contentStart, closeIdx).trim()
    result[key] = content
    result.sections.push({ tag: key, content })

    // Remove this tag block from remaining
    remaining = remaining.substring(0, openIdx) + remaining.substring(closeIdx + close.length)
  }

  // Parse <#TodoListPoint#> — check inside todoList first, then remaining, then raw
  if (result.todoList) {
    result.todoPoints = parseRepeatable(result.todoList, '<#TodoListPoint#>', '<#/TodoListPoint#>')
  }
  if (!result.todoPoints.length) {
    result.todoPoints = parseRepeatable(remaining, '<#TodoListPoint#>', '<#/TodoListPoint#>')
    if (result.todoPoints.length) {
      remaining = stripRepeatable(remaining, '<#TodoListPoint#>', '<#/TodoListPoint#>')
    }
  }
  if (!result.todoPoints.length) {
    result.todoPoints = parseRepeatable(raw, '<#TodoListPoint#>', '<#/TodoListPoint#>')
    if (result.todoPoints.length) {
      remaining = stripRepeatable(remaining, '<#TodoListPoint#>', '<#/TodoListPoint#>')
    }
  }
  if (result.todoPoints.length) {
    result.sections.push({ tag: 'todoPoints', content: result.todoPoints })
  }

  // Parse <#DetailPoint#> inside Detail block
  if (result.detail) {
    result.detailPoints = parseRepeatable(result.detail, '<#DetailPoint#>', '<#/DetailPoint#>')
  }

  // Clean point content: strip leading numbers (e.g. "1. Title" → "**Title**")
  result.todoPoints = result.todoPoints.map(stripLeadingNumber)
  result.detailPoints = result.detailPoints.map(stripLeadingNumber)

  result.remaining = remaining.trim()

  return result
}

/**
 * Strip leading number prefix (e.g. "1. Title…" → "**Title…**")
 * and bold the first line so the title stands out without redundant numbering.
 */
function stripLeadingNumber(text) {
  // Match leading "1." or "1)" patterns at the start
  const match = text.match(/^\d+[\.\)]\s*/)
  if (!match) return text

  const stripped = text.slice(match[0].length)
  // Bold the first line (title) if not already bold
  const lines = stripped.split('\n')
  const firstLine = lines[0].trim()
  if (firstLine && !firstLine.startsWith('**')) {
    lines[0] = `**${firstLine}**`
  }
  return lines.join('\n')
}

/**
 * Extract all occurrences of a repeatable tag pair from text.
 * Returns an array of trimmed content strings.
 */
function parseRepeatable(text, openTag, closeTag) {
  const items = []
  let cursor = 0
  while (cursor < text.length) {
    const openIdx = text.indexOf(openTag, cursor)
    if (openIdx === -1) break
    const contentStart = openIdx + openTag.length
    const closeIdx = text.indexOf(closeTag, contentStart)
    if (closeIdx === -1) break
    const content = text.substring(contentStart, closeIdx).trim()
    if (content) items.push(content)
    cursor = closeIdx + closeTag.length
  }
  return items
}

/**
 * Remove all occurrences of a repeatable tag pair from text.
 */
function stripRepeatable(text, openTag, closeTag) {
  let result = text
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const openIdx = result.indexOf(openTag)
    if (openIdx === -1) break
    const closeIdx = result.indexOf(closeTag, openIdx)
    if (closeIdx === -1) break
    result = result.substring(0, openIdx) + result.substring(closeIdx + closeTag.length)
  }
  return result
}
